import { format, subDays, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { memo } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface DateRangeState {
  from: Date | undefined;
  to: Date | undefined;
}

interface DateRangeFilterProps {
  dateRange: DateRangeState;
  onChange: (range: DateRangeState) => void;
  preset: string | null;
  onPresetChange: (preset: string | null) => void;
}

export const DateRangeFilter = memo(
  ({ dateRange, onChange, preset, onPresetChange }: DateRangeFilterProps) => {
    const handlePresetClick = (selectedPreset: string) => {
      onPresetChange(selectedPreset);

      const today = new Date();

      switch (selectedPreset) {
        case "today":
          onChange({ from: today, to: today });
          break;
        case "last_week":
          onChange({ from: subDays(today, 7), to: today });
          break;
        case "last_month":
          const lastMonth = subMonths(today, 1);
          onChange({
            from: startOfMonth(lastMonth),
            to: endOfMonth(lastMonth),
          });
          break;
        case "whole_time":
          onChange({ from: undefined, to: undefined });
          break;
      }
    };

    const handleCustomDateSelect = (
      type: "from" | "to",
      date: Date | undefined,
    ) => {
      onPresetChange("custom");
      onChange({
        ...dateRange,
        [type]: date,
      });
    };

    return (
      <div className="flex flex-col md:flex-row md:items-end gap-6 w-full">
        {/* ЛІВА ЧАСТИНА: Кнопки пресетів */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-muted-foreground">Date</label>
          <div className="flex flex-wrap items-center gap-2">
            <PresetButton
              label="Today"
              isActive={preset === "today"}
              onClick={() => handlePresetClick("today")}
            />
            <PresetButton
              label="Last week"
              isActive={preset === "last_week"}
              onClick={() => handlePresetClick("last_week")}
            />
            <PresetButton
              label="Last month"
              isActive={preset === "last_month"}
              onClick={() => handlePresetClick("last_month")}
            />
            <PresetButton
              label="Whole time"
              isActive={
                preset === "whole_time" ||
                (!dateRange.from && !dateRange.to && preset !== "custom")
              }
              onClick={() => handlePresetClick("whole_time")}
            />
          </div>
        </div>

        {/* ПРАВА ЧАСТИНА: Кастомний період (від і до) */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-muted-foreground">
            Custom period
          </label>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "h-9 w-[140px] cursor-pointer justify-between border-border bg-muted/60 text-xs font-normal text-muted-foreground",
                    dateRange.from && "text-foreground",
                  )}
                >
                  {dateRange.from ? (
                    format(dateRange.from, "dd/MM/yyyy")
                  ) : (
                    <span>DD/MM/YYYY</span>
                  )}
                  <CalendarIcon className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateRange.from}
                  onSelect={(date) => handleCustomDateSelect("from", date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <span className="font-medium text-muted-foreground/50">—</span>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "h-9 w-[140px] cursor-pointer justify-between border-border bg-muted/60 text-xs font-normal text-muted-foreground",
                    dateRange.to && "text-foreground",
                  )}
                >
                  {dateRange.to ? (
                    format(dateRange.to, "dd/MM/yyyy")
                  ) : (
                    <span>DD/MM/YYYY</span>
                  )}
                  <CalendarIcon className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateRange.to}
                  onSelect={(date) => handleCustomDateSelect("to", date)}
                  initialFocus
                  disabled={(date) =>
                    dateRange.from ? date < dateRange.from : false
                  }
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
    );
  },
);

DateRangeFilter.displayName = "DateRangeFilter";

function PresetButton({
  label,
  isActive,
  onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "cursor-pointer rounded-[18px] border border-border px-3 py-2 text-xs transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        isActive
          ? "border-primary/40 bg-accent font-medium text-accent-foreground"
          : "bg-muted text-muted-foreground hover:bg-muted/80",
      )}
    >
      {label}
    </button>
  );
}
